import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';

const POPUP_KEY = 'chichi_newsletter_popup';
const POPUP_DISMISS_DAYS = 7;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterPopup = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem(POPUP_KEY);
    let shouldShow = false;

    if (!stored) {
      shouldShow = true;
    } else if (stored === 'subscribed') {
      shouldShow = false;
    } else {
      // stored = timestamp of last dismiss — show again after POPUP_DISMISS_DAYS
      const dismissedAt = parseInt(stored, 10);
      if (!isNaN(dismissedAt)) {
        const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        shouldShow = daysSince >= POPUP_DISMISS_DAYS;
      }
    }

    if (shouldShow) {
      const timer = setTimeout(() => setVisible(true), 8000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(POPUP_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      await axios.post(`${API}/newsletter`, { email, nome: nome || undefined });
      setStatus('success');
      localStorage.setItem(POPUP_KEY, 'subscribed');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Si è verificato un errore. Riprova.';
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
            data-testid="newsletter-popup"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              <div
                className="relative h-28 flex items-end px-8 pb-6"
                style={{ backgroundColor: '#A67C52' }}
              >
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
                  data-testid="newsletter-popup-close"
                  aria-label="Chiudi"
                >
                  <X size={20} />
                </button>
                <p className="font-heading font-light text-2xl text-white tracking-wide">
                  Unisciti a noi
                </p>
              </div>

              <div className="px-8 py-6">
                {status === 'success' ? (
                  <div className="text-center py-4" data-testid="newsletter-success">
                    <p className="font-serif text-xl text-[#2C2C2C] mb-2">Benvenuta nella nostra famiglia!</p>
                    <p className="text-sm text-[#6B6B6B]">
                      Ti aggiorneremo su nuovi viaggi e storie. Grazie per esserti unita a noi.
                    </p>
                    <button
                      onClick={handleClose}
                      className="mt-5 text-sm text-[#A67C52] underline hover:text-[#8B6440] transition-colors"
                    >
                      Chiudi
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#6B6B6B] mb-5 leading-relaxed">
                      Iscriviti alla newsletter e ricevi i nostri racconti di viaggio in famiglia, consigli pratici e ispirazioni per la tua prossima avventura.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <input
                        type="text"
                        placeholder="Il tuo nome (opzionale)"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#A67C52]/30 transition-all"
                        data-testid="newsletter-nome-input"
                      />
                      <input
                        type="email"
                        placeholder="La tua email *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#A67C52]/30 transition-all"
                        data-testid="newsletter-email-input"
                      />

                      {status === 'error' && (
                        <p className="text-red-500 text-xs" data-testid="newsletter-error">
                          {errorMsg}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full bg-[#A67C52] hover:bg-[#8B6440] disabled:opacity-60 text-white font-medium py-3 rounded-lg transition-colors text-sm"
                        data-testid="newsletter-submit-btn"
                      >
                        {status === 'loading' ? 'Iscrizione in corso...' : 'Iscriviti alla newsletter'}
                      </button>
                    </form>

                    <p className="text-xs text-[#6B6B6B]/70 mt-3 text-center">
                      Niente spam, solo storie vere. Puoi disiscriverti in qualsiasi momento.
                    </p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPopup;
